import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPCAPI } from '../../../../../trpc/trpc';
import { PostsBoardFile } from '../postsModel';

/** A file that a draft, scheduled or "needs you" post uses: it stays on the board. */
export interface BlockedArchive {
  uuid: string;
  postUuids: string[];
}

export interface ArchivePostFiles {
  /** Archive (`true`) or Restore (`false`) files. Resolves with the number that changed. */
  run: (files: ReadonlyArray<Pick<PostsBoardFile, 'source' | 'uuid'>>, archived: boolean) => Promise<number>;
  isBusy: boolean;
  /** The files of the last call that a live post kept ("Cancel the post first"). */
  blocked: BlockedArchive[];
  /** True when the last call failed. */
  failed: boolean;
  clear: () => void;
}

/**
 * `assets.archive` (design assets-rethink c1, c2; owner decisions 3 and 4): one call for one file or
 * many. The board and the archive read again after it.
 */
export function useArchivePostFiles(accountId: number): ArchivePostFiles {
  const trpcAPI = useTRPCAPI();
  const queryClient = useQueryClient();
  const mutation = useMutation(trpcAPI.assets.archive.mutationOptions());
  const [blocked, setBlocked] = useState<BlockedArchive[]>([]);
  const [failed, setFailed] = useState(false);
  const { mutateAsync } = mutation;

  const run = useCallback(
    async (files: ReadonlyArray<Pick<PostsBoardFile, 'source' | 'uuid'>>, archived: boolean): Promise<number> => {
      if (files.length === 0) {
        return 0;
      }
      setFailed(false);
      setBlocked([]);
      try {
        const result = await mutateAsync({
          accountId,
          items: files.map((file) => ({ source: file.source, uuid: file.uuid })),
          archived,
        });
        setBlocked(result.blocked.map((one) => ({ uuid: one.uuid, postUuids: one.postUuids })));
        return result.changed;
      } catch (error) {
        console.error(error);
        setFailed(true);
        return 0;
      } finally {
        queryClient.invalidateQueries({ queryKey: trpcAPI.assets.posts.pathKey() }).catch(console.error);
        queryClient.invalidateQueries({ queryKey: trpcAPI.assets.archived.pathKey() }).catch(console.error);
      }
    },
    [accountId, mutateAsync, queryClient, trpcAPI]
  );

  const clear = useCallback(() => {
    setBlocked([]);
    setFailed(false);
  }, []);

  return { run, isBusy: mutation.isPending, blocked, failed, clear };
}
